import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Any one of these means the user works in CRM (Notes are shared CRM notes).
export const CRM_ANY = ["crm_dashboard.view", "enquiries.view", "customers.view", "contacts.view",
  "quotes.view", "pi.view", "tax_invoice.view"];

// perm is one key, or a list meaning "any of these"
export const canAccess = (has, perm) => (Array.isArray(perm) ? perm.some((k) => has(k)) : has(perm));

// path prefix -> required permission key. Ordered: most-specific prefixes first.
const ROUTE_PERMS = [
  ["/quotes/new", "quotes.create"],
  ["/quotes", "quotes.view"],
  ["/customers", "customers.view"],
  ["/email-drafts", "email_drafts.view"],
  ["/tally-pairing", "sales_orders.view"],
  ["/crm-cleanup", "customers.view"],
  ["/contacts", "contacts.view"],
  ["/products", "products.view"],
  ["/compound-costing", "compound_costing.view"],
  ["/product-costing", "product_costing.view"],
  ["/enquiries", "enquiries.view"],
  ["/pis", "pi.view"],
  ["/tax-invoices", "tax_invoice.view"],
  ["/sales-orders", "sales_orders.view"],
  ["/work-orders", "work_orders.view"],
  ["/mes/planning", "work_orders.view"],
  ["/mes/employees", "work_orders.view"],
  ["/mes/approvals", "work_orders.view"],
  ["/mes/register", "work_orders.view"],
  ["/mes/dashboard", "work_orders.view"],
  ["/mes/finished-goods", "work_orders.view"],
  ["/mes/transfers", "stock_transfer.view"],
  ["/production-planning", "production_dashboard.view"],
  ["/production", "production_dashboard.view"],
  ["/recipe-master", "recipe_master.view"],
  ["/raw-materials", "raw_material_master.view"],
  ["/mould-master", "mould_master.view"],
  ["/machine-master", "machine_master.view"],
  ["/machines", "machine_master.view"],
  ["/maintenance", "maintenance_dashboard.view"],
  ["/breakdowns", "breakdowns.view"],
  ["/pm-schedule", "preventive_maintenance.view"],
  ["/procurement", ["procurement_dashboard.view", "goods_receipt.view"]],
  ["/grn/new", "goods_receipt.create"],
  ["/grn", "goods_receipt.view"],
  ["/vendors", "vendors.view"],
  ["/rm-stock", "rm_stock.view"],
  ["/inventory/raw-materials", "raw_material_inventory.view"],
  ["/inventory/compound-production", "compound_production.view"],
  ["/inventory/physical-stock", "physical_stock.view"],
  ["/purchase/stock-dashboard", "inventory_dashboard.view"],
  ["/purchase/raw-materials", "raw_material_inventory.view"],
  ["/purchase/transfers", "stock_transfer.view"],
  ["/purchase/recommendations", "purchase_recommendation.view"],
  ["/purchase/ledger", "inventory_ledger.view"],
  ["/data-import", "data_import.view"],
  ["/compound-batches", "compound.view"],
  ["/production-jobs", "production.view"],
  ["/quality-control", "production_qc.view"],
  ["/finishing-challans/new", "finishing_challans.create"],
  ["/finishing-challans", "finishing_challans.view"],
  ["/qc/templates/new", "qc_templates.edit"],
  ["/qc/templates", "qc_templates.view"],
  ["/qc/settings", "qc_settings.edit"],
  ["/qc/fpa", "qc_fpa.view"],
  ["/qc/patrol", "qc_patrol.view"],
  ["/qc/night", ["qc_fpa.approve", "qc_final.approve", "qc_patrol.view"]],
  ["/qc/dashboard", ["qc_pdir.view", "qc_templates.view", "qc_fpa.view", "qc_patrol.view", "qc_final.view"]],
  ["/qc/final", "qc_final.view"],
  ["/capa/templates/new", "capa.templates"],
  ["/capa/new", "capa.create"],
  ["/capa", "capa.view"],
  ["/doc-dispatch/upload", "doc_dispatch.create"],
  ["/doc-dispatch/challans/new", "doc_dispatch.create"],
  ["/doc-dispatch/settings", "doc_dispatch.settings"],
  ["/doc-dispatch", "doc_dispatch.view"],
  ["/qc", "qc_pdir.view"],
  ["/finishing", "finishing.view"],
  ["/dispatch", "dispatch.view"],
  ["/accounts", "accounts_dashboard.view"],
  ["/ai/weight-calculator", "ai_weight_calculator.view"],
  ["/ai/mould-cost-estimator", "ai_product_finder.view"],
  ["/ai/product-finder", "ai_product_finder.view"],
  ["/tooling", "tooling_dashboard.view"],
  ["/purchase", "purchase_indents.view"],
  ["/users", "user_management.view"],
  ["/audit-log", "audit_log.view"],
  ["/reports", "reports.view"],
  ["/settings", "company_settings.view"],
  ["/followups", "enquiries.view"],
  ["/notes", CRM_ANY],
];

export function requiredPermFor(pathname) {
  const hit = ROUTE_PERMS.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix + "/"));
  return hit ? hit[1] : null;
}

// First module the user is allowed to land on (used for redirects). Returns a path whose guard passes.
export function firstAllowedPath(has) {
  if (has("crm_dashboard.view")) return "/home";
  if (has("production_dashboard.view")) return "/production";
  if (has("accounts_dashboard.view")) return "/accounts";
  if (has("dispatch.view")) return "/dispatch";
  if (has("tooling_dashboard.view")) return "/tooling";
  if (has("purchase_indents.view")) return "/purchase";
  if (has("work_orders.view")) return "/work-orders";
  if (has("sales_orders.view")) return "/sales-orders";
  if (has("finishing_challans.view")) return "/finishing-challans";
  if (has("qc_pdir.view")) return "/qc";
  if (has("qc_patrol.view")) return "/qc/patrol";
  if (has("qc_fpa.view")) return "/qc/fpa";
  if (has("qc_final.view")) return "/qc/final";
  if (has("qc_templates.view")) return "/qc/templates";
  if (has("capa.view")) return "/capa";
  if (has("doc_dispatch.view")) return "/doc-dispatch";
  // otherwise the first screen the user has any right to
  const hit = ROUTE_PERMS.find(([p, perm]) => !p.endsWith("/new") && canAccess(has, perm));
  return hit ? hit[0] : "/home";
}

export default function PermGuard() {
  const { has } = useAuth();
  const loc = useLocation();
  const perm = requiredPermFor(loc.pathname);
  if (perm && !canAccess(has, perm)) {
    const dest = firstAllowedPath(has);
    if (dest !== loc.pathname) return <Navigate to={dest} replace />;
  }
  return <Outlet />;
}
